import { CompareProduct } from '@/compare/types';
import { renderProductCard } from './render-product-card';
import { renderVariantRow } from './render-variant-row';
import { renderStickyHeader } from './render-sticky-header';
import { renderCompareGroup } from './render-compare-group';
import { renderApproximatePriceRow } from './render-approximate-price-row';
import { buildCompareData } from '../build/build-compare-data';
import { getCompareProducts, setCompareProducts, buildCompareUrl } from '../storage/compare-storage';

const MAX_COMPARE = 3;

function getSharedCompareProducts(): CompareProduct[] | null {
  const raw = sessionStorage.getItem('shared_compare_products');
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? (parsed as CompareProduct[]) : null;
  } catch {
    return null;
  }
}

function getCurrentCompareProducts(): CompareProduct[] {
  return getSharedCompareProducts() || getCompareProducts();
}

function setCurrentCompareProducts(products: CompareProduct[]) {
  if (getSharedCompareProducts()) {
    sessionStorage.setItem('shared_compare_products', JSON.stringify(products));
    return;
  }

  setCompareProducts(products);
}

interface ThemeStrings {
  bestFor: string;
  goodFor: string;
}

declare global {
  interface Window {
    themeStrings: ThemeStrings;
  }
}

/**
 * Renders the full comparison offcanvas content including tables and filters.
 */
export function renderCompareOffcanvasContent() {
  const content = document.getElementById('compare-drawer-results');
  const tagsContainer = document.getElementById('compare-product-tags');
  const tagsTitle = document.getElementById('compare-product-tags-title');

  if (!content) return;

  const products = getCurrentCompareProducts();

  if (products.length === 0) {
    if (tagsContainer) tagsContainer.innerHTML = '';
    content.innerHTML = `
      <div class="flex flex-col items-center justify-center h-full py-20x text-center">
        <p class="text-content-secondary mb-4x">Vertailulista on tyhjä</p>
        <button data-compare-drawer-close class="btn-primary">Palaa kauppaan</button>
      </div>`;
    return;
  }

  const normalizeTag = (tag: string): string => tag.trim().toLowerCase();

  // --- Tag Filtering Logic ---
  if (tagsContainer) {
    const compareTags = JSON.parse(tagsContainer.dataset.compareTags || '[]');
    const availableTags = compareTags.filter((compareTag: { tag: string }) =>
      products.some((product) =>
        (product.tags || []).some((productTag) => normalizeTag(productTag) === normalizeTag(compareTag.tag)),
      ),
    );

    if (availableTags.length === 0) {
      tagsContainer.classList.add('hidden');
      tagsTitle?.classList.add('hidden');
      tagsContainer.innerHTML = '';
    } else {
      tagsContainer.classList.remove('hidden');
      tagsTitle?.classList.remove('hidden');

      tagsContainer.innerHTML = `
        <div class="flex flex-nowrap gap-2x px-4x m:px-6x py-2x whitespace-nowrap">
          ${availableTags
            .map(
              (tag: { tag: string; label: string }) => `
                <button
                  type="button"
                  class="compare-tag-filter inline-flex items-center justify-center px-4x py-2x border-xs text-[14px] rounded-s
                        text-content-primary bg-primary border-primary hover:bg-hover
                        transition-colors duration-200 ease-in-out capitalize"
                  data-tag="${tag.tag}"
                >
                  ${tag.label}
                </button>
              `,
            )
            .join('')}
        </div>
      `;
    }
  }

  const stickyHeaderHtml = renderStickyHeader(products, MAX_COMPARE);

  // --- Comparison Table Building ---
  const cardsHtml = products.map(renderProductCard).join('');

  let placeholdersHtml = '';
  for (let i = products.length; i < MAX_COMPARE; i++) {
    placeholdersHtml += `
      <div class="flex flex-1 mx-2x shrink-0 w-[144px] m:w-[200px] h-[144px] m:h-[194px] flex-col items-center justify-center border-xs border-dashed border-tertiary rounded-m label-s text-content-tertiary bg-primary">
        ${i + 1}/${MAX_COMPARE}
      </div>
    `;
  }

  const { allGroupsMap } = buildCompareData(products);

  const allOptionTitles = Array.from(new Set(products.flatMap((p) => (p.optionNames || '').split('||')))).filter(
    (name) => name && name !== 'Title' && name !== 'Default Title',
  );

  const specsRows = allOptionTitles.map((title, i) => renderVariantRow(title, i, products)).join('');

  const specsTableHtml = `<div class="compare-group">${specsRows}</div>`;

  const compareFeaturesHtml = Array.from(allGroupsMap.keys())
    .map((groupName, index) => {
      const labels = allGroupsMap.get(groupName) || [];
      let approximatePriceRow = index === 0 ? renderApproximatePriceRow(products, MAX_COMPARE) : '';

      const groupHtml = renderCompareGroup({
        groupName,
        labels,
        products,
        maxCompare: MAX_COMPARE,
        approximatePriceRow,
      });

      if (index === 0) {
        return groupHtml + specsTableHtml;
      }

      return groupHtml;
    })
    .join('');

  content.innerHTML = `
    <div class="w-full relative">
      <div id="compare-sticky-header" class="overflow-x-hidden sticky top-0 left-0 right-0 z-10 bg-neutral-white border-b-xs border-tertiary opacity-0 invisible transition-opacity duration-300"></div>

      <div id="compare-scroll-container" class="w-full overflow-x-auto">
        <div class="flex flex-nowrap gap-2x m:gap-6x px-4x m:px-2x pt-8x pb-4x border-secondary compare-items-container">
          ${cardsHtml}
          ${placeholdersHtml}
        </div>
        <div class="compare-table-specs pb-4x">
          ${compareFeaturesHtml}
        </div>
      </div>
    </div>
  `;

  // --- Initialize Event Listeners ---
  initTagFiltering(products, normalizeTag);
  initDifferenceToggle();
  initCopyLinkLogic();
  initClearTableLogic();
  initVariantSwapping(content);
  initStickyHeaderScroll(content, stickyHeaderHtml);
  initRemoveButtons(content);
}

/**
 * INTERNAL HELPERS (Logic preservation)
 */

function initTagFiltering(products: CompareProduct[], normalizeTag: (t: string) => string) {
  const tagButtons = document.querySelectorAll<HTMLButtonElement>('.compare-tag-filter');
  const { themeStrings } = window;

  if (!themeStrings) return;

  tagButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const selectedTag = button.dataset.tag ?? '';
      if (!selectedTag) return;

      const isActive = button.classList.contains('bg-mono');
      const allChips = document.querySelectorAll<HTMLElement>('[data-product-chip]');
      const allSpacers = document.querySelectorAll<HTMLElement>('.compare-chip-spacer');

      tagButtons.forEach((btn) => {
        btn.classList.remove('bg-mono', 'text-content-primary-inverse');
        btn.classList.add('bg-primary', 'text-content-primary', 'hover:bg-hover');
      });

      allChips.forEach((chip) => {
        chip.classList.add('hidden');
        chip.innerHTML = '';
      });
      allSpacers.forEach((spacer) => spacer.classList.add('hidden'));

      if (isActive) return;

      const matchingProducts = products.filter((product) =>
        (product.tags || []).some((t) => normalizeTag(t) === normalizeTag(selectedTag)),
      );

      if (matchingProducts.length === 0) return;

      const isBestFor = matchingProducts.length === 1;

      allSpacers.forEach((spacer) => {
        const parent = spacer.parentElement;
        if (parent) {
          const productId = parent.dataset.mainCardId || parent.dataset.stickyId;
          const hasMatchingProduct = matchingProducts.some((p) => p.id.toString() === productId?.toString());

          if (!hasMatchingProduct) {
            spacer.classList.remove('hidden');
          }
        }
      });

      matchingProducts.forEach((product) => {
        const chips = document.querySelectorAll<HTMLElement>(`[data-product-chip="${product.id}"]`);
        chips.forEach((chip) => {
          chip.classList.remove('hidden');
          chip.innerHTML = `
            <div class="items-center gap-1x px-1x rounded-l bg-brand-subtle max-w-[184px]">
              <span class="label-s">
                ${isBestFor ? themeStrings.bestFor : themeStrings.goodFor}
                ${button.textContent?.trim() ?? ''}
              </span>
            </div>
          `;
        });
      });

      button.classList.remove('bg-primary', 'text-content-primary', 'hover:bg-hover');
      button.classList.add('bg-mono', 'text-content-primary-inverse');
    });
  });
}

function initDifferenceToggle() {
  document.addEventListener('change', (e) => {
    const target = e.target as HTMLInputElement;
    if (!target || target.id !== 'show-differences-toggle') return;

    const checked = target.checked;
    const rows = document.querySelectorAll('.compare-row');
    rows.forEach((row) => {
      const values = Array.from(row.querySelectorAll('.compare-value'))
        .map((c) => (c.textContent || '').replace(/\s+/g, ' ').trim())
        .filter((v) => v && v !== '—' && v !== '');

      if (values.length === 0) {
        (row as HTMLElement).classList.remove('hidden');
        return;
      }

      const allSame = values.every((v) => v === values[0]);
      (row as HTMLElement).classList.toggle('hidden', checked && allSame);
    });

    const groups = document.querySelectorAll('.compare-group');
    groups.forEach((group) => {
      const groupRows = group.querySelectorAll('.compare-row');
      if (groupRows.length === 0) return;
      const allRowsHidden = Array.from(groupRows).every((row) => row.classList.contains('hidden'));
      (group as HTMLElement).classList.toggle('hidden', checked && allRowsHidden);
    });
  });
}

function initCopyLinkLogic() {
  const copyLinkBtn = document.getElementById('compare-copy-link');
  const textElement = copyLinkBtn?.querySelector('.js-copy-text');

  copyLinkBtn?.addEventListener('click', async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!textElement) return;

    const url = buildCompareUrl(getCurrentCompareProducts());
    try {
      await navigator.clipboard.writeText(url);
      copyLinkBtn.classList.add('copied');
      const originalText = textElement.textContent;
      textElement.textContent = copyLinkBtn.getAttribute('data-copied-text');
      setTimeout(() => {
        textElement.textContent = originalText;
        copyLinkBtn.classList.remove('copied');
      }, 2000);
    } catch {
      /* ignored */
    }
  });
}

function initClearTableLogic() {
  const clearTableBtn = document.getElementById('compare-clear-table');
  clearTableBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCompareProducts([]);

    // Close modal logic
    const modal = document.getElementById('compare-drawer-modal') as HTMLDialogElement | null;
    const panel = document.getElementById('compare-drawer-panel');
    const backdrop = document.getElementById('compare-drawer-overlay');
    modal?.close();
    panel?.classList.add('translate-x-full');
    backdrop?.classList.add('hidden');
    document.body.classList.remove('scroll-lock');

    // Trigger re-renders (Need to ensure these are globally accessible or imported)
    renderCompareOffcanvasContent();
    // updateCompareToggles() and renderComparePanel() should be called from the main entry point
    const event = new CustomEvent('compare:updated');
    document.dispatchEvent(event);
  });
}

function initVariantSwapping(content: HTMLElement) {
  content.querySelectorAll('.variant-swap-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const b = btn as HTMLElement;
      const { productId, variantId, price, comparePrice } = b.dataset;
      const products = getCompareProducts();
      const product = products.find((p) => p.id === productId);

      if (product) {
        product.currentVariantId = variantId!;
        setCompareProducts(products);
      }

      const card = content.querySelector(`[data-main-card-id="${productId}"]`);
      if (card) {
        const pDisplay = card.querySelector('.main-price-display');
        const cDisplay = card.querySelector('.compare-price-display');
        if (pDisplay) pDisplay.textContent = price || '';
        if (cDisplay) {
          cDisplay.textContent = comparePrice || '';
          cDisplay.classList.toggle('hidden', !comparePrice || comparePrice === price);
        }
      }

      if (product) {
        const cell = b.closest('.flex-1');
        const display = cell?.querySelector('.current-val-display');
        const optionIndex = cell?.getAttribute('data-option-index');
        const active = product.variants?.find((v) => v.id === product.currentVariantId);

        if (display && active && optionIndex !== null) {
          const parts = Array.isArray(active.options) ? active.options : (active.options || '').split(' / ');
          display.textContent = parts?.[Number(optionIndex)]?.trim() || '—';
        }
      }

      // Sticky Header Price Update
      const sticky = document.querySelector(`[data-sticky-id="${productId}"] .sticky-price`);
      if (sticky) {
        const hasDiscount = comparePrice && comparePrice !== price;
        sticky.innerHTML = hasDiscount
          ? `<div class="flex items-center gap-1x"><span class="badge-discount px-1x rounded-sm"><span class="heading-xs">${price}</span></span><span class="label-xs line-through text-content-secondary">${comparePrice}</span></div>`
          : `<div class="heading-xs">${price}</div>`;
      }

      // Update button states
      b
        .closest('.flex')
        ?.querySelectorAll('.variant-swap-btn')
        .forEach((btnEl) => {
          const el = btnEl as HTMLElement;
          const isActive = el.dataset.variantId === variantId;
          el.classList.toggle('bg-mono', isActive);
          el.classList.toggle('text-content-primary-inverse', isActive);
          el.classList.toggle('border-mono', isActive);
          el.classList.toggle('bg-primary', !isActive);
          el.classList.toggle('text-content-primary', !isActive);
          el.classList.toggle('border-tertiary', !isActive);
        });
    });
  });
}

function initStickyHeaderScroll(content: HTMLElement, html: string) {
  const stickyHeader = document.querySelector('#compare-sticky-header') as HTMLElement;
  if (stickyHeader) {
    stickyHeader.style.display = 'none';
  }

  setTimeout(() => {
    const compareItemsContainer = document.querySelector('.compare-items-container') as HTMLElement;
    const scrollContainer = document.querySelector('#compare-scroll-container') as HTMLElement;

    if (stickyHeader && html) {
      stickyHeader.innerHTML = html;
    }

    if (!stickyHeader || !compareItemsContainer || !scrollContainer) {
      return;
    }

    scrollContainer.addEventListener(
      'scroll',
      () => {
        stickyHeader.style.transform = `translateX(-${scrollContainer.scrollLeft}px)`;
      },
      { passive: true },
    );

    const updateHeaderWidth = () => {
      const contentWidth = compareItemsContainer.scrollWidth;
      if (stickyHeader.firstElementChild) {
        (stickyHeader.firstElementChild as HTMLElement).style.minWidth = contentWidth + 'px';
      }
    };
    updateHeaderWidth();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            stickyHeader.style.display = 'block';
            requestAnimationFrame(() => {
              stickyHeader.classList.remove('opacity-0', 'invisible');
              stickyHeader.classList.add('opacity-100', 'visible');
            });
          } else {
            stickyHeader.classList.remove('opacity-100', 'visible');
            stickyHeader.classList.add('opacity-0', 'invisible');

            stickyHeader.addEventListener(
              'transitionend',
              () => {
                if (stickyHeader.classList.contains('opacity-0')) {
                  stickyHeader.style.display = 'none';
                }
              },
              { once: true },
            );
          }
        });
      },
      { threshold: 0 },
    );

    observer.observe(compareItemsContainer);
  }, 50);
}
function initRemoveButtons(content: HTMLElement) {
  content.querySelectorAll('.remove-compare-btn').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      const id = (e.currentTarget as HTMLElement).dataset.id;
      const updated = getCurrentCompareProducts().filter((prod) => prod.id !== id);
      setCurrentCompareProducts(updated);
      renderCompareOffcanvasContent();

      // Dispatch event to notify main panel and toggles
      document.dispatchEvent(new CustomEvent('compare:updated'));
    });
  });
}
